<script setup>
import {ref} from "vue";
import {useLanguageStore} from "@/pinia";
import {useToast} from "@/composables/useToast";

const toast = useToast();
const languageStore = useLanguageStore();

// Form ref
const formRef = ref(null);
const isFormValid = ref(false);

// Form data
const userId = ref("");
const userPwd = ref("");
const userPwdConfirm = ref("");
const userName = ref("");
const userEmail = ref("");
const userContact = ref("");
const userRole = ref("USER");

// Role options
const roleOptions = [
  { title: languageStore.get('admin'), value: 'ADMIN' },
  { title: languageStore.get('editor'), value: 'EDITOR' },
  { title: languageStore.get('user'), value: 'USER' },
];

// Validation rules
const rules = {
  required: v => !!v || languageStore.get('requiredField'),
  email: v => !v || /.+@.+\..+/.test(v) || languageStore.get('invalidEmail'),
  passwordMatch: v => v === userPwd.value || languageStore.get('passwordMismatch')
};

const props = defineProps({
  isUserInsertModal: Boolean,
})

const emits = defineEmits([
  'set_isUserInsertModal',
  'fn_saveUser'
])

const close = () => {
  userId.value = "";
  userPwd.value = "";
  userPwdConfirm.value = "";
  userName.value = "";
  userEmail.value = "";
  userContact.value = "";
  userRole.value = "USER";
  formRef.value?.reset();
  emits('set_isUserInsertModal', false);
}

const save = async () => {
  // v-form validation
  const {valid} = await formRef.value.validate();
  if (!valid) {
    toast.warning(languageStore.get('pleaseCheckInputFields'));
    return;
  }

  let newUser = {
    "userId": userId.value,
    "userPwd": userPwd.value,
    "userName": userName.value,
    "userEmail": userEmail.value,
    "userContact": userContact.value,
    "role": userRole.value,
    "isActive": true
  }
  emits('fn_saveUser', newUser);
  close();
}
</script>

<template>
  <v-dialog
      :model-value="isUserInsertModal"
      @update:model-value="close"
      width="500"
  >
    <v-card>
      <v-card-title class="modal-header">
        {{ languageStore.get('userRegistration') }}
      </v-card-title>

      <v-card-text class="modal-body">
        <v-form ref="formRef" v-model="isFormValid">
          <v-text-field
              v-model="userId"
              :label="languageStore.get('userId') + ' *'"
              variant="outlined"
              density="compact"
              :rules="[rules.required]"
          />

          <v-text-field
              v-model="userPwd"
              :label="languageStore.get('password') + ' *'"
              type="password"
              variant="outlined"
              density="compact"
              :rules="[rules.required]"
          />

          <v-text-field
              v-model="userPwdConfirm"
              :label="languageStore.get('confirmPassword') + ' *'"
              type="password"
              variant="outlined"
              density="compact"
              :rules="[rules.required, rules.passwordMatch]"
          />

          <v-text-field
              v-model="userName"
              :label="languageStore.get('name') + ' *'"
              variant="outlined"
              density="compact"
              :rules="[rules.required]"
          />

          <v-text-field
              v-model="userEmail"
              :label="languageStore.get('email') + ' *'"
              type="email"
              variant="outlined"
              density="compact"
              :rules="[rules.required, rules.email]"
          />

          <v-text-field
              v-model="userContact"
              :label="languageStore.get('phoneNumber')"
              variant="outlined"
              density="compact"
          />

          <v-select
              v-model="userRole"
              :label="languageStore.get('role') + ' *'"
              :items="roleOptions"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details="auto"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-spacer/>
        <v-btn
            color="primary"
            variant="flat"
            @click="save"
        >
          {{ languageStore.get('save') }}
        </v-btn>
        <v-btn
            variant="outlined"
            @click="close"
        >
          {{ languageStore.get('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 */
.modal-header {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 18px;
}

/* 모달 바디 - Vuetify 기본 padding 제거 */
:deep(.v-card-text.modal-body) {
  padding: 24px !important;
}

/* 모달 바디 내 입력 필드 간격 */
.modal-body :deep(.v-input) {
  margin-bottom: 16px;
}

.modal-body :deep(.v-input:last-child) {
  margin-bottom: 0;
}

/* 모달 푸터 - Vuetify 기본 padding 제거 */
:deep(.v-card-actions.modal-footer) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}
</style>
